import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import apiClient from '../apiClient';
import { CustomFieldDefinitionsApiResponse, CustomFieldsApiResponse, UpdateUserCustomFieldsRequest } from '../../types/customFields';

// Get all available custom field definitions
export const useGetCustomFieldDefinitions = () => {
    return useQuery<CustomFieldDefinitionsApiResponse>(
        ['customFieldDefinitions'],
        () => apiClient.get('/api/company/custom-fields/definitions').then((res) => res.data),
        {
            staleTime: 10 * 60 * 1000, // 10 minutes - field definitions don't change often
            cacheTime: 30 * 60 * 1000, // 30 minutes
        }
    );
};

// Get user's custom field values
export const useGetUserCustomFields = (userId: number, enabled: boolean = true) => {
    return useQuery<CustomFieldsApiResponse>(
        ['userCustomFields', userId],
        () => apiClient.get(`/api/company/employees/${userId}/custom-fields`).then((res) => res.data),
        {
            enabled: enabled && !!userId,
            staleTime: 2 * 60 * 1000, // 2 minutes
            cacheTime: 5 * 60 * 1000, // 5 minutes
        }
    );
};

// Update user's custom field values
export const useUpdateUserCustomFields = (
    onSuccess?: (data: CustomFieldsApiResponse, variables: { userId: number; data: UpdateUserCustomFieldsRequest }) => void,
    onError?: (error: AxiosError) => void
) => {
    const queryClient = useQueryClient();

    return useMutation<CustomFieldsApiResponse, AxiosError, { userId: number; data: UpdateUserCustomFieldsRequest }>(
        ({ userId, data }) => apiClient.put(`/api/company/employees/${userId}/custom-fields`, data).then((res) => res.data),
        {
            onSuccess: (data, variables) => {
                // Invalidate and refetch user custom fields
                queryClient.invalidateQueries(['userCustomFields', variables.userId]);

                // Also invalidate user profile if it includes custom fields
                queryClient.invalidateQueries(['userProfile', variables.userId]);

                onSuccess?.(data, variables);
            },
            onError: (error) => {
                onError?.(error);
            },
        }
    );
};

// Delete a specific custom field value
export const useDeleteUserCustomField = (
    onSuccess?: (variables: { userId: number; fieldName: string }) => void,
    onError?: (error: AxiosError) => void
) => {
    const queryClient = useQueryClient();

    return useMutation<void, AxiosError, { userId: number; fieldName: string }>(
        ({ userId, fieldName }) => apiClient.delete(`/api/company/employees/${userId}/custom-fields/${fieldName}`).then((res) => res.data),
        {
            onSuccess: (data, variables) => {
                // Invalidate and refetch user custom fields
                queryClient.invalidateQueries(['userCustomFields', variables.userId]);

                // Also invalidate user profile
                queryClient.invalidateQueries(['userProfile', variables.userId]);

                onSuccess?.(variables);
            },
            onError: (error) => {
                onError?.(error);
            },
        }
    );
};

// Combined hook for getting both definitions and user values
export const useUserCustomFieldsWithDefinitions = (userId: number, enabled: boolean = true) => {
    const definitions = useGetCustomFieldDefinitions();
    const userFields = useGetUserCustomFields(userId, enabled);

    return {
        definitions: definitions.data?.data || [],
        userFields: userFields.data?.data || [],
        warnings: userFields.data?.warnings || [],
        isLoading: definitions.isLoading || userFields.isLoading,
        isError: definitions.isError || userFields.isError,
        error: definitions.error || userFields.error,
        refetch: () => {
            definitions.refetch();
            userFields.refetch();
        },
    };
};
